// frontend/src/stores/feature.ts
// Central feature flags and social/AI commerce features store

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiClient } from '@/services/api'

export interface PublicFeatureConfig {
  groupBuy: {
    enabled: boolean
    minParticipants: number
    defaultDurationHours: number
    autoRefundOnFail: boolean
  }
  aiValuation: {
    enabled: boolean
    dailyUserLimit: number
  }
  priceDropAlert: {
    enabled: boolean
    minDropPct: number
  }
  sellerComparison: {
    enabled: boolean
    minSellerCount: number
  }
  aiAssistant: {
    enabled: boolean
    showSuggestions: boolean
  }
  videoListing: {
    enabled: boolean
    maxDurationSeconds: number
    maxFileSizeMb: number
  }
  splashScreen: {
    enabled: boolean
    durationMs: number
    oncePerSession: boolean
    showSkipButton: boolean
    taglineText?: string
  }
  // Admin "Security" gates, so pages can explain what is needed before an
  // action instead of after the server refuses it (403 + code).
  settlement?: {
    paymentDeadlineHours: number
    autoReleaseDays: number
  }
  // Seller's fee on a completed sale (Admin > Settings > Platform).
  fees?: {
    commissionRatePct: number
  }
  requirements?: {
    emailVerifiedToSell: boolean
    kycRequiredToSell: boolean
    emailVerifiedToBid: boolean
    kycRequiredToBid: boolean
  }
  // The real admin-configured soft-close window (see bidModel.placeBidAtomic),
  // so the bid sheet's anti-sniping copy states an actual number.
  antiSniping?: {
    triggerMinutes: number
    extensionMinutes: number
  }
  // Admin Settings > Platform & Contact — the single source of truth for the
  // storefront's Contact page (used to be hardcoded i18n strings unrelated
  // to this admin panel, so editing it there had no visible effect).
  // Admin Settings > Platform: brand name, home title, meta description.
  // title/description: one entry per language, empty = built-in translation.
  site?: {
    name: string
    title: { ru: string; ky: string; tr: string }
    description: { ru: string; ky: string; tr: string }
  }
  contact?: {
    supportEmail: string
    supportPhone: string
    whatsappNumber: string
    address: string
  }
}

export interface BankGateway {
  id: string
  name: string
  shortName: string
  active: boolean
  badge: string
  type: 'qr' | 'card' | 'escrow' | 'wallet' | 'cash'
  color: string
  desc: string
  instructions?: string
}

export interface PublicBankConfig {
  activeBanks: BankGateway[]
  hasActiveBanks: boolean
  defaultNotice: string
  supportPhone: string
  whatsappNumber: string
  telegramHandle: string
}

export interface GroupBuyData {
  id: string
  auctionId: string
  // Viewer flags; other buyers are never named or identified.
  createdByYou?: boolean
  youJoined?: boolean
  targetParticipants: number
  currentParticipants: number
  tierPrice1: number
  tierPrice5: number
  tierPrice10: number
  currentPrice: number
  status: 'active' | 'completed' | 'failed' | 'expired'
  expiresAt: string
  createdAt: string
  participants: Array<{
    name: string // initials only, e.g. "А*** Ж."
    isYou: boolean
    joinedAt: string
    paymentStatus?: string // the viewer's own entry only
  }>
}

export interface MatchingSeller {
  id: string
  title: string
  currentPrice: number
  startingPrice: number
  sellerId: string
  sellerName: string
  sellerRating: number
  city: string
  deliveryDays: number
  images: string[]
  isBestOffer: boolean
}

export interface AiValuationResult {
  suggestedTitle: string
  suggestedCategory: string
  // 'new' | 'good' from the server; the screen translates it.
  condition: 'new' | 'good'
  estimatedMarketValueMin: number
  estimatedMarketValueMax: number
  recommendedStartPrice: number
  recommendedBuyNowPrice: number
  currency: string
  // How many comparable listings the estimate is based on — real, unlike the
  // fabricated "94% confidence" this used to carry.
  basedOnListings: number
  // 'listings' = matching iTorgo lots; 'ai' = the AI's estimate of the local
  // second-hand price; 'none' = no price could be given (prices are 0).
  priceSource: 'listings' | 'ai' | 'none'
  // Why there is no price: 'needName' = the photo shows what kind of thing it
  // is but not which one (ask for brand and model); 'needDetails' = cars,
  // property, machinery; 'unknown'. Empty when there is a price.
  noPriceReason: '' | 'needName' | 'needDetails' | 'unknown'
  // What the seller can do next, most useful first: codes the dialog has
  // words for ('labelElectronics', 'photo_dark', 'modelLabel' + brand...).
  suggestions: Array<{ code: string; params?: Record<string, string> }>
  notes: string
  // Whether a real vision model actually looked at the photo, or this is the
  // recent-listings-average fallback used when no AI provider is configured.
  aiPowered: boolean
  // True only when a photo was uploaded and analyzing it failed (wrong model,
  // provider outage) — distinct from aiPowered:false with no photo at all, so
  // the seller can be told "we tried and it didn't work" rather than nothing.
  visionFailed: boolean
}

export interface AiAssistantResponse {
  reply: string
  recommendedAuctions: Array<{
    id: string
    title: string
    currentPrice: number
    category: string
    city: string
    imageUrl: string
  }>
  quickSuggestions: string[]
}

export const useFeatureStore = defineStore('feature', () => {
  const config = ref<PublicFeatureConfig>({
    groupBuy: {
      enabled: true,
      minParticipants: 3,
      defaultDurationHours: 24,
      autoRefundOnFail: true
    },
    aiValuation: {
      enabled: true,
      dailyUserLimit: 5
    },
    priceDropAlert: {
      enabled: true,
      minDropPct: 5
    },
    sellerComparison: {
      enabled: true,
      minSellerCount: 2
    },
    aiAssistant: {
      enabled: true,
      showSuggestions: true
    },
    videoListing: {
      enabled: true,
      maxDurationSeconds: 30,
      maxFileSizeMb: 25
    },
    splashScreen: {
      enabled: true,
      durationMs: 900,
      oncePerSession: true,
      showSkipButton: true,
      taglineText: 'КЫРГЫЗСТАН • REAL-TIME AUCTION PLATFORM'
    },
    settlement: {
      paymentDeadlineHours: 72,
      autoReleaseDays: 7
    },
    requirements: {
      emailVerifiedToSell: false,
      kycRequiredToSell: false,
      emailVerifiedToBid: false,
      kycRequiredToBid: false
    },
    antiSniping: {
      triggerMinutes: 2,
      extensionMinutes: 2
    },
    // Until the real config arrives (or if it never does), show no phone or
    // address rather than invented ones; the Contact page hides empty rows.
    contact: {
      supportEmail: 'info@itorgo.com',
      supportPhone: '',
      whatsappNumber: '',
      address: ''
    }
  })

  const isLoading = ref(false)
  const isInitialized = ref(false)

  // Reactive Feature Flag Getters
  const isGroupBuyEnabled = computed(() => config.value.groupBuy?.enabled ?? true)
  const isAiValuationEnabled = computed(() => config.value.aiValuation?.enabled ?? true)
  const isPriceDropAlertEnabled = computed(() => config.value.priceDropAlert?.enabled ?? true)
  const isSellerComparisonEnabled = computed(() => config.value.sellerComparison?.enabled ?? true)
  const isAiAssistantEnabled = computed(() => config.value.aiAssistant?.enabled ?? true)
  const isVideoListingEnabled = computed(() => config.value.videoListing?.enabled ?? true)
  const isSplashScreenEnabled = computed(() => config.value.splashScreen?.enabled ?? true)
  const antiSnipingExtensionMinutes = computed(() => config.value.antiSniping?.extensionMinutes ?? 2)
  const splashScreenConfig = computed(() => config.value.splashScreen || {
    enabled: true,
    durationMs: 900,
    oncePerSession: true,
    showSkipButton: true,
    taglineText: 'КЫРГЫЗСТАН • REAL-TIME AUCTION PLATFORM'
  })

  // Bank Gateways Configuration
  const banksConfig = ref<PublicBankConfig>({
    activeBanks: [],
    hasActiveBanks: false,
    defaultNotice: 'В настоящее время прямые банковские шлюзы находятся в процессе официальной интеграции и тестирования.',
    supportPhone: '+996 507 975 873',
    whatsappNumber: '+996 507 975 873',
    telegramHandle: '@itorgo_support'
  })

  const activeBanks = computed(() => banksConfig.value.activeBanks || [])
  const hasActiveBanks = computed(() => activeBanks.value.length > 0)

  // Fetch Public Flags from Backend
  async function fetchFeaturesConfig() {
    isLoading.value = true
    try {
      const res = await apiClient.get<any>('/api/config/features')
      if (res.data && res.data.success && res.data.data) {
        config.value = res.data.data
        isInitialized.value = true
      }
    } catch (err) {
      console.warn('[FeatureStore] Using default feature config:', err)
    } finally {
      isLoading.value = false
    }
  }

  async function fetchBanksConfig() {
    try {
      const res = await apiClient.get<any>('/api/config/banks')
      if (res.data && res.data.success && res.data.data) {
        banksConfig.value = res.data.data
      }
    } catch (err) {
      console.warn('[FeatureStore] Using default banks config:', err)
    }
  }

  // Feature 11: Group Buy Actions
  async function getAuctionGroupBuy(auctionId: string): Promise<GroupBuyData | null> {
    try {
      const res = await apiClient.get<any>(`/api/auctions/${auctionId}/group-buy`)
      if (res.data && res.data.success && res.data.data) {
        return res.data.data
      }
      return null
    } catch (err) {
      console.error('[FeatureStore] getAuctionGroupBuy failed:', err)
      return null
    }
  }

  async function joinGroupBuy(groupBuyId: string): Promise<any> {
    const res = await apiClient.post<any>(`/api/group-buys/${groupBuyId}/join`)
    return res.data
  }

  // Feature 13: AI Valuation Action
  async function evaluateProduct(formData: FormData): Promise<AiValuationResult> {
    const res = await apiClient.post<any>('/api/ai/evaluate-product', formData)
    if (res.data && res.data.success && res.data.data) {
      // The server's own field names (estimatedPriceRange.min/max, a nested
      // suggestedStartingPrice.amount, ...) — mapped once here so the modal
      // that renders this never has to know the API shape.
      const d = res.data.data
      const result: AiValuationResult = {
        suggestedTitle: d.suggestedTitle,
        suggestedCategory: d.category,
        condition: d.condition === 'new' ? 'new' : 'good',
        estimatedMarketValueMin: d.estimatedPriceRange?.min ?? 0,
        estimatedMarketValueMax: d.estimatedPriceRange?.max ?? 0,
        recommendedStartPrice: d.suggestedStartingPrice?.amount ?? 0,
        recommendedBuyNowPrice: d.suggestedBuyNowPrice?.amount ?? 0,
        currency: d.estimatedPriceRange?.currency || 'KGS',
        basedOnListings: d.basedOnListings ?? 0,
        priceSource: d.priceSource === 'listings' || d.priceSource === 'ai' ? d.priceSource : 'none',
        noPriceReason: ['needName', 'needDetails', 'unknown'].includes(d.noPriceReason) ? d.noPriceReason : '',
        suggestions: Array.isArray(d.suggestions) ? d.suggestions.filter((a: any) => a && typeof a.code === 'string') : [],
        notes: d.suggestedDescription,
        aiPowered: !!d.aiPowered,
        visionFailed: !!d.visionFailed,
      }
      return result
    }
    throw new Error(res.data?.error || res.data?.message || 'AI valuation failed')
  }

  // Feature 14: Price Drop Alert Actions
  async function createPriceAlert(auctionId: string, targetPrice?: number, channels?: any) {
    const res = await apiClient.post<any>(`/api/auctions/${auctionId}/price-alert`, {
      targetPrice,
      channels
    })
    return res.data
  }

  async function getUserPriceAlerts() {
    const res = await apiClient.get<any>('/api/user/price-alerts')
    return res.data?.data || []
  }

  async function deletePriceAlert(alertId: string) {
    const res = await apiClient.delete<any>(`/api/user/price-alerts/${alertId}`)
    return res.data
  }

  // Feature 16: Multi-Seller Comparison
  async function getMatchingSellers(auctionId: string): Promise<MatchingSeller[]> {
    try {
      const res = await apiClient.get<any>(`/api/auctions/${auctionId}/sellers`)
      if (res.data && res.data.success && Array.isArray(res.data.data)) {
        return res.data.data
      }
      return []
    } catch (err) {
      console.error('[FeatureStore] getMatchingSellers failed:', err)
      return []
    }
  }

  // Feature 17: AI Shopping Assistant
  async function askAiAssistant(message: string, history: Array<{ role: string; content: string }> = [], locale?: string): Promise<AiAssistantResponse> {
    const res = await apiClient.post<any>('/api/ai/shopping-assistant', {
      query: message,
      message,
      history,
      locale: locale || 'ru'
    })
    if (res.data && res.data.success && res.data.data) {
      return res.data.data
    }
    throw new Error(res.data?.message || 'AI assistant request failed')
  }

  return {
    config,
    isLoading,
    isInitialized,

    // Flags
    isGroupBuyEnabled,
    isAiValuationEnabled,
    isPriceDropAlertEnabled,
    isSellerComparisonEnabled,
    isAiAssistantEnabled,
    isVideoListingEnabled,
    isSplashScreenEnabled,
    splashScreenConfig,
    antiSnipingExtensionMinutes,
    banksConfig,
    activeBanks,
    hasActiveBanks,

    // Actions
    fetchFeaturesConfig,
    fetchBanksConfig,
    getAuctionGroupBuy,
    joinGroupBuy,
    evaluateProduct,
    createPriceAlert,
    getUserPriceAlerts,
    deletePriceAlert,
    getMatchingSellers,
    askAiAssistant
  }
})

export default useFeatureStore
